/* eslint-disable no-plusplus */
import ConfettiParticle from '@/models/ConfettiParticle';

export class ConfettiService {
    private static serviceInstance: ConfettiService;

    public static get instance() {
        if (!this.serviceInstance) {
            this.serviceInstance = new this();
        }
        return this.serviceInstance;
    }

    private canvas: HTMLCanvasElement | null = null;

    private context: CanvasRenderingContext2D | null = null;

    private maxConfettis: number = 150;

    private particles: ConfettiParticle[] = [];

    private possibleColors: string[] = [
        'DodgerBlue',
        'OliveDrab',
        'Gold',
        'Pink',
        'SlateBlue',
        'LightBlue',
        'Gold',
        'Violet',
        'PaleGreen',
        'SteelBlue',
        'SandyBrown',
        'Chocolate',
        'Crimson'
    ];

    private animationFrameId: number | null = null;

    private shouldGenerateConfetti: boolean = true; // New flag

    private initCanvas(container: HTMLElement, height: number) {
        this.canvas = document.createElement('canvas');
        this.context = this.canvas.getContext('2d')!;
        this.canvas.width = container.clientWidth;
        this.canvas.height = height; // Set canvas height based on the option provided
        this.canvas.style.position = 'absolute';
        this.canvas.style.top = '0';
        this.canvas.style.left = '0';
        this.canvas.style.pointerEvents = 'none';
        container.appendChild(this.canvas);

        window.addEventListener('resize', () => {
            if (this.canvas) {
                this.canvas.width = container.clientWidth;
                this.canvas.height = height; // Keep the canvas height consistent with the provided option
            }
        }, false);
    }

    private draw() {
        if (!this.canvas || !this.context) return;

        this.context.clearRect(0, 0, this.canvas.width, this.canvas.height);

        for (let i = 0; i < this.particles.length; i++) {
            const particle = this.particles[i];
            particle.draw(this.context);

            particle.tiltAngle += particle.tiltAngleIncremental;
            particle.y += (Math.cos(particle.d) + 3 + particle.r / 2) / 2;
            particle.tilt = Math.sin(particle.tiltAngle - i / 3) * 15;

            if (particle.y > this.canvas.height) {
                this.particles.splice(i, 1);
                i--;
                // eslint-disable-next-line no-continue
                continue;
            }

            if (particle.x > this.canvas.width + 30 || particle.x < -30) {
                particle.x = Math.random() * this.canvas.width;
                particle.y = -30;
                particle.tilt = Math.floor(Math.random() * 10) - 20;
            }
        }

        if (!this.shouldGenerateConfetti && this.particles.length === 0) {
            this.stopConfetti();
        }
    }

    private animateConfetti() {
        this.draw();
        this.animationFrameId = requestAnimationFrame(this.animateConfetti.bind(this));
    }

    public startConfetti(container: HTMLElement, options: { height?: number } = {}) {
        const height = options.height || container.clientHeight; // Use provided height or default to container's clientHeight

        if (!this.canvas) {
            this.initCanvas(container, height);
        }

        this.shouldGenerateConfetti = true;
        this.particles = [];
        for (let i = 0; i < this.maxConfettis; i++) {
            this.particles.push(new ConfettiParticle(this.canvas!.width, height, this.possibleColors, this.maxConfettis));
        }

        this.animateConfetti();

        setTimeout(() => {
            this.shouldGenerateConfetti = false;
        }, 3000);
    }

    public stopConfetti() {
        if (this.animationFrameId) {
            cancelAnimationFrame(this.animationFrameId);
            this.animationFrameId = null;
        }
        if (this.context && this.canvas) {
            this.context.clearRect(0, 0, this.canvas.width, this.canvas.height);
            this.canvas.remove();
            this.canvas = null;
        }
    }
}

export const confettiService = ConfettiService.instance;
